import { blue, teal, indigo, blueGrey, grey } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import StorageIcon from "@mui/icons-material/Storage";
import SearchIcon from "@mui/icons-material/Search";
import DataArrayIcon from "@mui/icons-material/DataArray";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import TuneIcon from "@mui/icons-material/Tune";
import RadarIcon from "@mui/icons-material/Radar";
import { motion } from "framer-motion";

// Animation speeds with sensible durations for a professional application
export const ANIMATION_SPEED = {
  SLOW: "slow",
  MEDIUM: "medium",
  FAST: "fast",
};

// Helper to get duration based on speed setting
const getDuration = (speed) => {
  switch (speed) {
    case ANIMATION_SPEED.SLOW:
      return 3;
    case ANIMATION_SPEED.FAST:
      return 1.5;
    case ANIMATION_SPEED.MEDIUM:
    default:
      return 2.2;
  }
};

// Satellite animation - simulates satellite orbit
const SatelliteAnimation = ({
  color = blue[700],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        y: [0, -10, 0],
        x: [0, 10, 0, -10, 0],
        rotate: [0, 5, 0, -5, 0],
      }}
      transition={{
        repeat: Infinity,
        duration: getDuration(speed) * 1.2,
        ease: "easeInOut",
      }}
    >
      <SatelliteAltIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Radar animation - simulates scanning or data acquisition
const RadarAnimation = ({
  color = indigo[600],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        rotate: [0, 180, 360],
        scale: [1, 1.05, 1],
      }}
      transition={{
        rotate: {
          repeat: Infinity,
          duration: getDuration(speed) * 1.5,
          ease: "linear",
        },
        scale: {
          repeat: Infinity,
          duration: getDuration(speed),
          ease: "easeInOut",
        },
      }}
    >
      <RadarIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Cloud sync - represents data synchronization
const CloudSyncAnimation = ({
  color = blue[600],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{ rotate: 360 }}
      transition={{
        repeat: Infinity,
        duration: getDuration(speed) * 1.5,
        ease: "easeInOut",
      }}
    >
      <CloudSyncIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Data processing animation
const DataProcessingAnimation = ({
  color = teal[700],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        rotateY: [0, 180, 360],
        scale: [1, 1.05, 1],
      }}
      transition={{
        rotateY: {
          repeat: Infinity,
          duration: getDuration(speed) * 1.8,
          ease: "easeInOut",
        },
        scale: {
          repeat: Infinity,
          duration: getDuration(speed),
          ease: "easeInOut",
        },
      }}
    >
      <DataArrayIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Database query animation
const DatabaseAnimation = ({
  color = blueGrey[600],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        y: [0, -5, 0],
        opacity: [0.9, 1, 0.9],
      }}
      transition={{
        repeat: Infinity,
        duration: getDuration(speed),
        ease: "easeInOut",
      }}
    >
      <StorageIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Search data animation
const SearchAnimation = ({
  color = blue[800],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        scale: [1, 1.08, 1],
        rotate: [0, 10, 0, -10, 0],
      }}
      transition={{
        scale: {
          repeat: Infinity,
          duration: getDuration(speed),
          ease: "easeInOut",
        },
        rotate: {
          repeat: Infinity,
          duration: getDuration(speed) * 1.2,
          ease: "easeInOut",
        },
      }}
    >
      <SearchIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Data download animation
const DownloadAnimation = ({
  color = teal[600],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        y: [0, 5, 0],
        opacity: [0.85, 1, 0.85],
      }}
      transition={{
        repeat: Infinity,
        duration: getDuration(speed) * 0.8,
        ease: "easeInOut",
      }}
    >
      <CloudDownloadIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Processing animation - rotating autorenew icon
const ProcessingAnimation = ({
  color = indigo[500],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{ rotate: 360 }}
      transition={{
        repeat: Infinity,
        duration: getDuration(speed),
        ease: "linear",
      }}
    >
      <AutorenewIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Parameter adjustment animation
const ParameterAnimation = ({
  color = blueGrey[700],
  size = 100,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <motion.div
      style={{ width: "fit-content" }}
      animate={{
        rotate: [-5, 5, -5],
        scale: [1, 1.05, 1],
      }}
      transition={{
        repeat: Infinity,
        duration: getDuration(speed),
        ease: "easeInOut",
      }}
    >
      <TuneIcon sx={{ color: color, fontSize: size }} />
    </motion.div>
  );
};

// Complex data processing animation - combines two icons
const ComplexDataAnimation = ({
  size = 120,
  speed = ANIMATION_SPEED.MEDIUM,
}) => {
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <motion.div
        style={{
          position: "absolute",
        }}
        animate={{
          rotate: 360,
          scale: [1, 1.05, 1],
        }}
        transition={{
          rotate: {
            repeat: Infinity,
            duration: getDuration(speed) * 2,
            ease: "linear",
          },
          scale: {
            repeat: Infinity,
            duration: getDuration(speed) * 1.3,
            ease: "easeInOut",
          },
        }}
      >
        <DataObjectIcon sx={{ color: teal[700], fontSize: size * 0.8 }} />
      </motion.div>

      <motion.div
        style={{
          position: "absolute",
        }}
        animate={{
          rotate: -360,
          opacity: [0.7, 0.9, 0.7],
        }}
        transition={{
          rotate: {
            repeat: Infinity,
            duration: getDuration(speed) * 3,
            ease: "linear",
          },
          opacity: {
            repeat: Infinity,
            duration: getDuration(speed) * 1.5,
            ease: "easeInOut",
          },
        }}
      >
        <SatelliteAltIcon sx={{ color: blue[600], fontSize: size }} />
      </motion.div>
    </div>
  );
};

// Create a set of professionally appropriate loading messages for data processing applications
export const loadingText = [
  {
    icon: <SatelliteAnimation color={blue[700]} size={100} />,
    top_text: "Connecting to Data Source...",
    bottom_text:
      "Establishing a secure data connection. Grab a coffee ☕ - this might take a moment!",
  },
  {
    icon: <RadarAnimation color={indigo[600]} size={100} />,
    top_text: "Scanning Archives...",
    bottom_text: "Searching through data archives for the latest datasets.",
  },
  {
    icon: <CloudSyncAnimation color={blue[600]} size={100} />,
    top_text: "Synchronizing Data...",
    bottom_text:
      "Pulling data from various servers—feel free to take a quick stretch break 😎",
  },
  {
    icon: <DataProcessingAnimation color={teal[700]} size={100} />,
    top_text: "Processing Data...",
    bottom_text: "Crunching numbers and tidying up results. Almost there!",
  },
  {
    icon: <DatabaseAnimation color={blueGrey[600]} size={100} />,
    top_text: "Querying Database...",
    bottom_text: "Searching our vast catalogues for matching datasets.",
  },
  {
    icon: <SearchAnimation color={blue[800]} size={100} />,
    top_text: "Executing Search Query...",
    bottom_text: "Finding data that matches your parameters.",
  },
  {
    icon: <DownloadAnimation color={teal[600]} size={100} />,
    top_text: "Preparing Data Transfer...",
    bottom_text: "Organizing your requested datasets for download.",
  },
  {
    icon: <ProcessingAnimation color={indigo[500]} size={100} />,
    top_text: "Computing Results...",
    bottom_text: "Running complex calculations and analyses.",
  },
  {
    icon: <ParameterAnimation color={blueGrey[700]} size={100} />,
    top_text: "Adjusting Parameters...",
    bottom_text: "Fine-tuning filters for better results.",
  },
  {
    icon: <ComplexDataAnimation size={100} />,
    top_text: "Analyzing Complex Data...",
    bottom_text: "Processing multi-layered data streams.",
  },
];

// Long-running operation specific messages
export const longRunningText = [
  {
    icon: (
      <ProcessingAnimation
        color={indigo[700]}
        size={100}
        speed={ANIMATION_SPEED.SLOW}
      />
    ),
    top_text: "Processing Large Query...",
    bottom_text:
      "This is a heavy operation—consider taking a coffee break ☕ while we work our magic!",
  },
  {
    icon: <ComplexDataAnimation size={100} speed={ANIMATION_SPEED.SLOW} />,
    top_text: "Retrieving  Results...",
    bottom_text:
      "Complex algorithms in progress. Patience is key – maybe enjoy a quick snack 🍪!",
  },

  {
    icon: <ComplexDataAnimation size={100} speed={ANIMATION_SPEED.SLOW} />,
    top_text: "You may have slow internet",
    bottom_text: "Consider upgrading your wifi... 🙃",
  },
  {
    icon: (
      <CloudSyncAnimation
        color={blue[700]}
        size={100}
        speed={ANIMATION_SPEED.SLOW}
      />
    ),
    top_text: "Retrieving Historical Data...",
    bottom_text: "Accessing archived records. Hang tight and relax a bit 😊",
  },
];

// Export individual animations for reuse
export const satelliteAnimations = {
  SatelliteAnimation,
  RadarAnimation,
  CloudSyncAnimation,
  DataProcessingAnimation,
  DatabaseAnimation,
  SearchAnimation,
  DownloadAnimation,
  ProcessingAnimation,
  ParameterAnimation,
  ComplexDataAnimation,
};

// Helper function to get a random loading animation
export const getRandomLoadingAnimation = () => {
  const len = loadingText.length;
  return loadingText[Math.floor(Math.random() * len)];
};

// Helper function to get a long-running operation message
export const getLongRunningAnimation = () => {
  const len = longRunningText.length;
  return longRunningText[Math.floor(Math.random() * len)];
};
